import classnames from 'classnames';
import React, { useState, useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { AlertLooks } from '../../../../enums/alert.js';
import { FontIcons } from '../../../../enums/font-icon.js';
import { baseAlertStyle, getAlertTheme } from '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import { fontFamilyMedium } from '../../../../themes/fonts.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import '@brandfolder/utilities';
import { CloseButton } from '../../button/close-button/close-button.js';
import '../../button/color-button/color-button.js';
import '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../tooltip/hover-tooltip/hover-tooltip.js';
import { FontIcon } from '../../icon/font-icon/font-icon.js';
import 'react-laag';
import 'react-transition-group';
import '../../button/icon-button/icon-button.js';
import '../../button/standard-button/standard-button.js';
import '../../loader/circle-loader/circle-loader.js';
import '../../loader/shimmer-loader/shimmer-loader.js';
import { BaseAlert } from '../base-alert/base-alert.js';

const BaseStandardAlert = (props)=>{
    const { bodyWrapperProps, children, className, closeButtonProps, heading, headingWrapperProps, icon, iconProps, iconWrapperProps, labels = {}, look, messageWrapperProps, onClose, showClose = false, showHeading = true, showIcon = true, ...otherProps } = props;
    const { closeButtonLabel } = labels;
    const [closed, setClosed] = useState(false);
    const getIcon = ()=>{
        if (icon) return icon;
        switch(look){
            case AlertLooks.Error:
                {
                    return FontIcons.Alert;
                }
            case AlertLooks.Success:
                {
                    return FontIcons.CheckMarkCircle;
                }
            case AlertLooks.Warning:
                {
                    return FontIcons.Warning;
                }
            default:
                {
                    // AlertLooks.None
                    return null;
                }
        }
    };
    const fontIcon = getIcon();
    const handleClose = ()=>{
        setClosed(true);
        if (onClose) onClose();
    };
    if (closed) return null;
    return /*#__PURE__*/ React.createElement(BaseAlert, {
        ...otherProps,
        className: classnames({
            'bf-alert__standard': true,
            [`bf-alert__standard--${look}`]: look !== AlertLooks.None,
            'bf-alert__standard--has-close-button': showClose,
            'bf-alert__standard--has-heading': showHeading,
            'bf-alert__standard--has-icon': showIcon,
            [`${className}`]: !!className
        })
    }, showIcon && fontIcon && /*#__PURE__*/ React.createElement("div", {
        ...iconWrapperProps,
        className: classnames({
            'bf-alert__icon': true,
            [`${iconWrapperProps?.className}`]: !!iconWrapperProps?.className
        })
    }, /*#__PURE__*/ React.createElement(FontIcon, {
        ...iconProps,
        icon: fontIcon,
        iconSize: iconProps?.iconSize || 18
    })), /*#__PURE__*/ React.createElement("div", {
        ...bodyWrapperProps,
        className: classnames({
            'bf-alert__body': true,
            [`${bodyWrapperProps?.className}`]: !!bodyWrapperProps?.className
        })
    }, heading && showHeading && /*#__PURE__*/ React.createElement("div", {
        ...headingWrapperProps,
        className: classnames({
            'bf-alert__heading': true,
            [`${headingWrapperProps?.className}`]: !!headingWrapperProps?.className
        })
    }, heading), /*#__PURE__*/ React.createElement("div", {
        ...messageWrapperProps,
        className: classnames({
            'bf-alert__message': true,
            [`${messageWrapperProps?.className}`]: !!messageWrapperProps?.className
        })
    }, children)), showClose && closeButtonLabel && /*#__PURE__*/ React.createElement(CloseButton, {
        ...closeButtonProps,
        "aria-label": closeButtonLabel,
        icon: FontIcons.XCirlce,
        onClick: ()=>{
            handleClose();
        },
        onKeyUp: (e)=>{
            if (e.key === 'Enter') {
                handleClose();
            }
            if (closeButtonProps?.onKeyUp) {
                closeButtonProps?.onKeyUp(e);
            }
        }
    }));
};
const Alert = styled(BaseStandardAlert)`
  ${baseAlertStyle}
  ${({ look })=>getAlertTheme({
        look
    })}
  display: flex;

  .bf-alert__body {
    flex: 1 1 0%;
  }

  .bf-alert__heading {
    font-family: ${fontFamilyMedium};
    font-size: 16px;
    line-height: 1.4;
    margin-bottom: 5px;
  }

  .bf-alert__icon {
    flex-shrink: 0;
    margin-top: 1px;
    margin-inline-end: 10px;
    min-width: 0;
  }

  .bf-alert__message {
    white-space: normal;
  }

  .bf-button__close {
    align-self: flex-start;
    flex-shrink: 0;
    margin: -4px -4px -4px 0;
    padding: 4px;

    .bf-button__close-icon {
      font-size: 22px;
    }
  }
`;
const StandardAlert = (props)=>{
    const { look = AlertLooks.Warning, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Alert, {
        ...otherProps,
        look: look
    }));
};

export { StandardAlert };
//# sourceMappingURL=standard-alert.js.map
